import type { ObstacleBody } from '../types';
import type { Vec2 } from './vec2';
import { OBSTACLE_RESTITUTION, PLAYER_RADIUS } from './constants';

/** A body that can hit obstacles: position plus slide velocity. */
interface SlidingBody {
  x: number;
  y: number;
  vx: number;
  vy: number;
}

/**
 * Earliest t in [0, 1] at which the segment p0 → p1 enters the circle (c, R), or null if it
 * never does. A segment that starts inside (or on) the circle returns 0; a tangent touch counts
 * as a hit. Used for swept warlock slides (R = o.r + PLAYER_RADIUS), projectile sweeps
 * (R = o.r + projectile radius) and line of sight (R = o.r).
 */
export function segmentCircleToi(
  p0x: number,
  p0y: number,
  p1x: number,
  p1y: number,
  cx: number,
  cy: number,
  R: number,
): number | null {
  const dx = p1x - p0x;
  const dy = p1y - p0y;
  const fx = p0x - cx;
  const fy = p0y - cy;
  const k = fx * fx + fy * fy - R * R;
  if (k <= 0) return 0;
  const a = dx * dx + dy * dy;
  if (a === 0) return null;
  const b = 2 * (fx * dx + fy * dy);
  const disc = b * b - 4 * a * k;
  if (disc < 0) return null;
  const t = (-b - Math.sqrt(disc)) / (2 * a);
  return t >= 0 && t <= 1 ? t : null;
}

/** Where a swept circle first meets a stone: the path fraction and the stone. */
export interface StoneHit {
  toi: number;
  obstacle: ObstacleBody;
}

/**
 * The first obstacle a circle of `radius` sweeping p0 → p1 touches, or null. Ties go to the
 * earlier obstacle in the list. `radius` 0 is a line-of-sight test. Used for projectiles,
 * Lightning, the Meteor cover rule and bot line-of-sight checks.
 */
export function firstStoneHit(
  p0: Vec2,
  p1: Vec2,
  radius: number,
  obstacles: readonly ObstacleBody[],
): StoneHit | null {
  let best: StoneHit | null = null;
  for (const o of obstacles) {
    const toi = segmentCircleToi(p0.x, p0.y, p1.x, p1.y, o.x, o.y, o.r + radius);
    if (toi !== null && (!best || toi < best.toi)) best = { toi, obstacle: o };
  }
  return best;
}

/**
 * Unit normal from the obstacle centre to the body; +x when exactly centred, the same
 * convention as `circleVsCircle`.
 */
function contactNormal(
  body: SlidingBody,
  o: ObstacleBody,
): { nx: number; ny: number; dist: number } {
  const dx = body.x - o.x;
  const dy = body.y - o.y;
  const dist = Math.hypot(dx, dy);
  if (dist === 0) return { nx: 1, ny: 0, dist: 0 };
  return { nx: dx / dist, ny: dy / dist, dist };
}

/** Reflect the normal part of the velocity if the body is moving into the obstacle. */
function bounce(body: SlidingBody, nx: number, ny: number): void {
  const vn = body.vx * nx + body.vy * ny;
  if (vn >= 0) return;
  const j = (1 + OBSTACLE_RESTITUTION) * vn;
  body.vx -= j * nx;
  body.vy -= j * ny;
}

/**
 * Integrate one step of slide velocity, stopping at the first obstacle the body would enter.
 * On contact the body stops on the obstacle's surface, bounces (restitution
 * OBSTACLE_RESTITUTION, tangential speed kept), and the rest of this step's travel is dropped.
 * Obstacles the body is already leaving are ignored, so a body resting against a stone can
 * slide away from it. With no obstacles this is exactly `x += vx·dt`. With `bounce` false the
 * body stops dead at the contact point instead (a blocked Thrust). Returns the obstacle hit.
 */
export function sweepSlide(
  body: SlidingBody,
  dt: number,
  obstacles: readonly ObstacleBody[],
  bounceOff = true,
): ObstacleBody | null {
  const dx = body.vx * dt;
  const dy = body.vy * dt;
  let t = 1;
  let hit: ObstacleBody | null = null;
  for (const o of obstacles) {
    // Only obstacles the body is moving towards.
    if ((body.x - o.x) * dx + (body.y - o.y) * dy >= 0) continue;
    const toi = segmentCircleToi(
      body.x,
      body.y,
      body.x + dx,
      body.y + dy,
      o.x,
      o.y,
      o.r + PLAYER_RADIUS,
    );
    if (toi !== null && toi < t) {
      t = toi;
      hit = o;
    }
  }
  if (!hit) {
    body.x += dx;
    body.y += dy;
    return null;
  }
  body.x += dx * t;
  body.y += dy * t;
  if (bounceOff) {
    const { nx, ny } = contactNormal(body, hit);
    bounce(body, nx, ny);
  } else {
    body.vx = 0;
    body.vy = 0;
  }
  return hit;
}

/**
 * Push a warlock out of every obstacle it overlaps (obstacles never move) and bounce any
 * velocity into them. Tangential velocity is kept, so a glancing hit slides around the stone,
 * and a walking warlock (no velocity) is pushed along the normal around the stone's edge.
 * `passes` sweeps handle a body wedged between two touching obstacles. Returns true on contact.
 */
export function resolveObstacleContacts(
  body: SlidingBody,
  obstacles: readonly ObstacleBody[],
  passes = 2,
): boolean {
  let touched = false;
  for (let pass = 0; pass < passes; pass++) {
    let moved = false;
    for (const o of obstacles) {
      const { nx, ny, dist } = contactNormal(body, o);
      const overlap = o.r + PLAYER_RADIUS - dist;
      if (overlap <= 0) continue;
      body.x += nx * overlap;
      body.y += ny * overlap;
      bounce(body, nx, ny);
      moved = true;
    }
    if (!moved) break;
    touched = true;
  }
  return touched;
}
